import { formatISO } from 'date-fns';
import { useConfigContext } from '../../contexts/ConfigContext';
import { SettingsContextAnalyticsStatus, useSettingsContext } from '../../contexts/SettingsContext';
import { Logger } from '../../utils/logger';
import { AnalyticsEventAction } from './analytics.types';
import { mapToPlausibleAnalyticsEventAction } from '../../utils/analytics';

// We are using Google Analytics 4:
// https://developers.google.com/analytics/devguides/collection/ga4/events?client_type=gtag
// https://support.google.com/analytics/answer/9303323?hl=en#zippy=%2Cin-this-article
// Consent management: https://developers.google.com/tag-platform/devguides/consent + https://support.google.com/analytics/answer/9976101#tag_behavior
// If the consent is denied google is modelling data based on pings: https://support.google.com/analytics/answer/11161109
// GDPR: https://www.cookiebot.com/en/google-analytics-gdpr/
// There is a limit of 100 properties for us (will be increased as Analytics 360 customer)
// Each property can contain 50 datastreams (A datastream gives us a measurement id that we use to collect data)
// What the "gtag('js', new Date())" does: https://stackoverflow.com/questions/59256532/what-is-the-js-gtags-js-command
// GA standard user_engagement event: https://support.google.com/analytics/answer/11109416#user-engagement

const logger = new Logger('UseAnalytics');

function useAnalytics() {
  const { analytics } = useSettingsContext();
  const { id } = useConfigContext();

  function trackAnalyticsEvent(eventAction: AnalyticsEventAction) {
    if (analytics === SettingsContextAnalyticsStatus.ENABLED) {
      const { type, ...params } = eventAction;

      if (!window.gtag) {
        logger.warn('gtag is not defined');
      } else {
        logger.debug('trackGoogleAnalyticsEvent:', type, params);
        window.gtag('event', type, params);
      }
    }

    if (window.plausible) {
      const plausibleEventAction = mapToPlausibleAnalyticsEventAction(eventAction);
      if (!plausibleEventAction) return;
      const { type, ...params } = plausibleEventAction;
      logger.debug('trackPlausibleEvent:', eventAction);
      window.plausible(type, { props: { event: JSON.stringify({ pId: id, date: formatISO(new Date()).split('+')[0], ...params }) } });
    }
  }

  return { trackAnalyticsEvent };
}

export default useAnalytics;
